/**
 * MODULE: core/tenant/frontend/components/TenantProvider.tsx
 * PURPOSE: Tenant context provider - Manages tenant state and provides tenant context to child components
 *
 * DEPENDENCIES:
 *   - react: createContext, useCallback, useEffect, useMemo, useState, ReactNode
 *   - ../api/tenantApi: tenantApi for API calls
 *   - @core/auth: useAuth for authentication state (Vite alias points to frontend directory)
 *   - ../../contracts/tenant.types: Tenant, TenantContextValue, TenantState types
 *
 * PUBLIC API:
 *   - TenantProvider: React context provider component
 *   - TenantContext: React context for tenant state
 *
 * SIDE EFFECTS:
 *   - Reads/writes localStorage (current_tenant_id key)
 *   - Makes API calls to load tenants (tenantApi.list())
 *   - Makes API calls to switch tenants (tenantApi.switch())
 *   - Updates React context state
 *   - Triggers re-renders when tenant state changes
 *
 * AGENT SAFETY: SAFE
 * FILE SIZE: 126 / 200 lines
 *
 * CHANGE LOG:
 *   - 2026-01-20: Created - Tenant context provider
 *     Manages current tenant state and available tenants list
 *     Loads tenants on authentication
 *     Persists selected tenant in localStorage
 *     Provides switchTenant() and refreshTenants() functions
 *     Related: TenantSwitcher, TenantGuard, useTenant hook
 *   - 2026-01-21: Fixed import path - Changed from @core/auth/frontend to @core/auth
 *     Vite alias @core/auth already points to frontend directory, so /frontend suffix was incorrect
 *
 * See: /README.md#documentation | /ARCHITECTURE.md | /core/tenant/README.md
 */

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { tenantApi } from '../api/tenantApi';
import { useAuth } from '@core/auth';
import type {
  Tenant,
  TenantContextValue,
  TenantState,
} from '../../contracts/tenant.types';

const TENANT_STORAGE_KEY = 'current_tenant_id';
/** Must stay in sync with AuthProvider / getTenantId() (gateway JWT tenant context). */
const AUTH_TENANT_ID_KEY = 'tenant_id';

function bareTenantUuid(id: string): string {
  return id.trim().replace(/^tnt_/, '');
}

function tenantIdsMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return bareTenantUuid(a) === bareTenantUuid(b);
}

/** Persist selection for TenantSwitcher and API X-Tenant-ID (JWT-aligned). */
function persistTenantSelection(tenant: Tenant): void {
  localStorage.setItem(TENANT_STORAGE_KEY, tenant.id);
  localStorage.setItem(AUTH_TENANT_ID_KEY, bareTenantUuid(tenant.id));
}

function resolveCurrentTenant(
  tenants: Tenant[],
  authTenantId: string | undefined,
  storedTenantId: string | null
): Tenant | undefined {
  if (authTenantId) {
    const fromJwt = tenants.find((t) => tenantIdsMatch(t.id, authTenantId));
    if (fromJwt) return fromJwt;
  }
  if (storedTenantId) {
    const fromStorage = tenants.find((t) => tenantIdsMatch(t.id, storedTenantId));
    if (fromStorage) return fromStorage;
  }
  return tenants[0];
}

const initialState: TenantState = {
  currentTenant: null,
  availableTenants: [],
  isLoading: true,
  error: null,
};

export const TenantContext = createContext<TenantContextValue | null>(null);

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const [state, setState] = useState<TenantState>(initialState);
  const { isAuthenticated, user } = useAuth();

  // Load tenants when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setState(initialState);
      return;
    }

    const loadTenants = async () => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const tenants = await tenantApi.list();
        const storedTenantId = localStorage.getItem(TENANT_STORAGE_KEY);
        const currentTenant = resolveCurrentTenant(tenants, user?.tenant_id, storedTenantId);

        if (currentTenant) {
          persistTenantSelection(currentTenant);
        }

        setState({
          currentTenant: currentTenant || null,
          availableTenants: tenants,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load tenants',
        }));
      }
    };

    loadTenants();
  }, [isAuthenticated, user?.tenant_id]);

  const switchTenant = useCallback(async (tenantId: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const tenant = await tenantApi.switch(tenantId);
      persistTenantSelection(tenant);

      setState((prev) => ({
        ...prev,
        currentTenant: tenant,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to switch tenant',
      }));
      throw error;
    }
  }, []);

  const refreshTenants = useCallback(async () => {
    try {
      const tenants = await tenantApi.list();
      setState((prev) => ({
        ...prev,
        availableTenants: tenants,
      }));
    } catch (error) {
      console.error('Failed to refresh tenants:', error);
    }
  }, []);

  const value = useMemo<TenantContextValue>(
    () => ({
      ...state,
      switchTenant,
      refreshTenants,
    }),
    [state, switchTenant, refreshTenants]
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}
